import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import { View } from 'react-native';
import {
  BodyText,
  SectionHeader,
  theme,
  useResponsive,
  VStack,
} from '@sg/garnish';

import {
  CheckoutLayoutBlock,
  SweetpassCheckoutCheckbox,
  SweetpassMembershipPlanSelector,
} from '@order/components';
import { DurationUnit, type SubscriptionCreditCard } from '@order/graphql';
import { type SweetpassMembershipPlans } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import { SweetpassCheckoutCardBlock } from '../SweetpassCheckoutCardBlock';
import { SweetpassCheckoutCta } from '../SweetpassCheckoutCta';
import { SweetpassCheckoutPrice } from '../SweetpassCheckoutPrice';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutBlock = (props: SweetpassCheckoutBlockProps) => {
  const {
    testID,
    termsAndConditions,
    cta,
    planPrice,
    billingFrequencyUnit = DurationUnit.Month,
    hasFreeTrial,
    isLoading,
    hasAcceptedTerms,
    canCheckout,
    membershipPlansData,
    creditCardsData,
    handleAddCreditCard,
    handleAcceptTerms,
    handleCta,
  } = props;

  const { t } = useLocalizationContext();
  const { match, currentBreakpoint } = useResponsive();

  const { selectedCreditCard, creditCards, onSelectedCreditCardChange } =
    creditCardsData;

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldRenderMembershipPlanSelector = membershipPlansData !== undefined;
  const shouldRenderPaymentMethodsSelector =
    shouldRenderMembershipPlanSelector && currentBreakpoint.isXS;

  // ─── Styles ──────────────────────────────────────────────────────────

  const checkoutPriceStyles = [
    styles.lineItem,
    match([styles.lineItemXS, styles.lineItemSM]),
  ];
  const membershipPlanSelectorContainerStyles = match([
    {},
    styles.membershipPlanSelectorContainerSM,
  ]);

  const responsiveStackSpacing = match([
    theme.spacing['10'],
    theme.spacing['8'],
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CheckoutLayoutBlock testID={testID}>
      <VStack gap={responsiveStackSpacing}>
        {/* ─── Multiple Membership Plans Selector ────── */}

        {shouldRenderMembershipPlanSelector ? (
          <View style={membershipPlanSelectorContainerStyles}>
            <SweetpassMembershipPlanSelector
              plans={membershipPlansData.plans}
              plansFeaturedText={membershipPlansData.freeTrialNote}
              selectedPlanId={membershipPlansData.selectedPlanId}
              onSelectedPlanChange={membershipPlansData.onSelectedPlanIdChange}
            />
          </View>
        ) : null}

        {/* ─── Payment Method Selector ───────────────── */}

        {/*
            NOTE: When we render the multiple membership plan selector, we must
                  display the payment method selector on the smallest breakpoint
                  following the selector.
        */}
        {shouldRenderPaymentMethodsSelector ? (
          <SweetpassCheckoutCardBlock
            selectedCreditCard={selectedCreditCard}
            creditCards={creditCards}
            handleSelectCreditCard={onSelectedCreditCardChange}
            handleAddCreditCard={handleAddCreditCard}
          />
        ) : null}

        {/* ─── Single Plan Summary  ──────────────────── */}

        {shouldRenderMembershipPlanSelector ? null : (
          <View>
            <SectionHeader heading={t('sweetpass.checkout.your-purchase')} />

            <View style={checkoutPriceStyles}>
              <BodyText bold>{t('sweetpass-plus')}</BodyText>

              <SweetpassCheckoutPrice
                planPrice={planPrice}
                billingFrequencyUnit={billingFrequencyUnit}
                hasFreeTrial={hasFreeTrial}
              />
            </View>
          </View>
        )}

        {/* ─── Terms and Conditions ──────────────────── */}

        <SweetpassCheckoutCheckbox
          termsAndConditions={termsAndConditions}
          hasAcceptedTerms={hasAcceptedTerms}
          handleAcceptTerms={handleAcceptTerms}
        />
      </VStack>

      <View style={styles.footer}>
        <SweetpassCheckoutCta
          testID="sweetpass.checkout.cta-desktop"
          isLoading={isLoading}
          disabled={!canCheckout}
          cta={cta}
          onPress={handleCta}
        />
      </View>
    </CheckoutLayoutBlock>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassCheckoutBlockProps = Readonly<{
  termsAndConditions: string;
  cta: string;
  planPrice: number;
  hasFreeTrial: boolean;
  billingFrequencyUnit?: DurationUnit;
  isLoading: boolean;
  hasAcceptedTerms: boolean;
  canCheckout: boolean;
  handleAcceptTerms: (hasAcceptedTerms: boolean) => void;
  handleCta: () => void;
  handleAddCreditCard: () => void;
  creditCardsData: {
    creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>>;
    selectedCreditCard?: Partial<SubscriptionCreditCard>;
    onSelectedCreditCardChange: (
      creditCard: Partial<SubscriptionCreditCard>,
    ) => void;
  };
  membershipPlansData?: {
    plans: SweetpassMembershipPlans;
    selectedPlanId?: string;
    onSelectedPlanIdChange: (planId: string) => void;
    freeTrialNote?: string;
  };
}> &
  Pick<ViewProps, 'testID'>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  lineItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.TEXT_COLOR,
  },
  lineItemXS: {
    paddingVertical: theme.spacing['6'],
  },
  lineItemSM: {
    paddingVertical: theme.spacing['10'],
  },
  membershipPlanSelectorContainerSM: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
    paddingBottom: theme.spacing['6'],
  },
  footer: {
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
});
